import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Droplets, Shield, Thermometer, Zap, Clock, CheckCircle2, 
  Phone, Mail, MapPin, ChevronDown, ChevronUp, Star,
  Users, Award, Leaf, Timer, ArrowRight, Play, Sparkles,
  Building2, Home, Factory, Layers, Check, X, AlertTriangle,
  Umbrella, Sun, Snowflake, Wind, Target, TrendingUp, Calendar,
  BadgeCheck, Wrench, Eye, CloudRain, Gauge, CircleDollarSign
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Info
const COMPANY = {
  name: "RevoGreen Srl",
  brand: "RevoUltra®",
  address: "Via Roma 44B, 37060 Mozzecane (VR)",
  piva: "P.IVA 04582700232",
  phone: "800 86 46 21",
  whatsapp: "379 161 7599",
  email: "info@revogreenitalia.com",
  website: "www.revogreenitalia.com"
};

// Video URLs
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_revo-ultra/artifacts/y5qsgu4e_grok-video-c42a4e1a-9a42-447b-a9bc-1b82dbcbf7ce%20%283%29.mp4";
const PROMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_revo-ultra/artifacts/3kvd2w3n_grok-video-c42a4e1a-9a42-447b-a9bc-1b82dbcbf7ce%20%284%29.mp4";

// Video Modal Component
const VideoModal = ({ isOpen, onClose }) => {
  const modalVideoRef = useRef(null);

  useEffect(() => {
    if (isOpen && modalVideoRef.current) {
      modalVideoRef.current.play().catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div 
        className="relative w-full max-w-5xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
          data-testid="close-video-modal"
        >
          <X className="w-8 h-8" />
        </button>
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
          <video 
            ref={modalVideoRef}
            controls
            autoPlay
            playsInline
            className="w-full"
          >
            <source src={PROMO_VIDEO_URL} type="video/mp4" />
            Il tuo browser non supporta i video.
          </video>
        </div>
      </div>
    </div>
  );
};

// Brand Text Component
const BrandText = ({ variant = "default" }) => (
  <span className={`font-semibold tracking-tight ${variant === "dark" ? "text-white" : "text-[#1D1D1F]"}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
    <span className={variant === "dark" ? "text-white" : "text-[#1D1D1F]"}>REVO</span>
    <span className="text-[#15803d]">ULTRA</span>
    <sup className="text-xs">®</sup>
  </span>
);

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count.toLocaleString('it-IT')}{suffix}</span>;
};

// Scroll Reveal Hook
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="faq-item" data-testid="faq-item">
    <button className="faq-question w-full text-left text-lg" onClick={onClick} data-testid="faq-btn">
      <span>{question}</span>
      {isOpen ? <ChevronUp className="w-5 h-5 text-[#15803d]" /> : <ChevronDown className="w-5 h-5 text-[#86868B]" />}
    </button>
    <div className={`overflow-hidden transition-all duration-500 ${isOpen ? 'max-h-[500px] opacity-100 pt-4' : 'max-h-0 opacity-0'}`}>
      <p className="text-[#86868B] leading-relaxed">{answer}</p>
    </div>
  </div>
);

// Urgency Banner Component
const UrgencyBanner = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 59, seconds: 59 });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-2 px-3 md:py-3 md:px-4 text-center text-xs md:text-base font-medium">
      <div className="container-main flex items-center justify-center gap-2 md:gap-4">
        <span className="flex items-center gap-1 md:gap-2">
          <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 animate-pulse flex-shrink-0" />
          <span><strong>ATTENZIONE:</strong> <span className="hidden sm:inline">Solo </span>7 sopralluoghi gratis<span className="hidden sm:inline"> disponibili</span></span>
        </span>
        <span className="hidden md:inline">|</span>
        <span className="hidden md:flex items-center gap-2 font-mono">
          <Timer className="w-4 h-4" />
          Scade tra: {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

// Main App
function App() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [formData, setFormData] = useState({ nome: '', telefono: '', email: '', provincia: '', messaggio: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [calcMq, setCalcMq] = useState(150);
  const [heroRef, heroVisible] = useScrollReveal();
  const [featuresRef, featuresVisible] = useScrollReveal();
  const [calcRef, calcVisible] = useScrollReveal();
  const [scrolled, setScrolled] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const videoRef = useRef(null);

  // Force video play on mount
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.play().catch(() => {});
    }
  }, []);

  // Track scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, { 
        ...formData, 
        email: formData.email || 'non-fornita@revoultra.it',
        cap: formData.cap || '00000' 
      });
      // Track Lead event with Meta Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'Hero Form', content_category: 'Preventivo' });
      }
      // Track conversion with Google Ads
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { 'send_to': 'AW-17960264240/lead_hero_form' });
      }
      setFormSubmitted(true);
    } catch (error) {
      console.error('Form error:', error);
      setFormSubmitted(true);
    }
  };

  // Pricing tiers
  const getPricePerMq = (mq) => {
    if (mq <= 150) return 90;
    if (mq <= 300) return 80;
    if (mq <= 1000) return 70;
    return 50;
  };

  const [calcFormData, setCalcFormData] = useState({ nome: '', telefono: '', email: '', tipo: '' });
  const [calcUnlocked, setCalcUnlocked] = useState(false);
  const [showFinalPrice, setShowFinalPrice] = useState(false);
  
  // Step 2 additional data
  const [tipologiaTetto, setTipologiaTetto] = useState('');
  const [fotoTetto, setFotoTetto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [fasciaOraria, setFasciaOraria] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [coords, setCoords] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Roof types with icons
  const tipiTetto = [
    { id: 'falda', nome: 'Tetto a Falda', icon: Home },
    { id: 'piano', nome: 'Tetto Piano', icon: Building2 },
    { id: 'capannone', nome: 'Capannone', icon: Factory },
    { id: 'terrazzo', nome: 'Terrazzo', icon: Layers }
  ];

  const fasceOrarie = [
    { id: 'mattina', label: '9:00 - 12:00' },
    { id: 'pranzo', label: '12:00 - 14:00' },
    { id: 'pomeriggio', label: '14:00 - 18:00' },
    { id: 'sera', label: '18:00 - 20:00' }
  ];

  const handleFotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoTetto(file);
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        },
        (error) => console.log('Geolocation error:', error)
      );
    }
  };

  const getFullPricing = (mq, tipoCliente) => {
    const prezzoBase = mq * getPricePerMq(mq);
    const ivaRate = tipoCliente === 'privato' ? 0.10 : 0.22;
    const markup = 0.35;
    
    const prezzoListino = Math.round(prezzoBase * (1 + markup) * (1 + ivaRate));
    const prezzoReale = Math.round(prezzoBase * (1 + ivaRate));
    const risparmio = prezzoListino - prezzoReale;
    const scontoPercent = Math.round((risparmio / prezzoListino) * 100);
    
    return { prezzoListino, prezzoReale, risparmio, scontoPercent, ivaLabel: tipoCliente === 'privato' ? 'IVA 10%' : 'IVA 22%' };
  };

  const handleCalcFormSubmit = async (e) => {
    e.preventDefault();
    if (!calcFormData.tipo) {
      alert('Seleziona se sei un privato o un\'azienda');
      return;
    }
    try {
      await axios.post(`${API}/leads`, { 
        ...calcFormData, 
        provincia: 'Da Calcolatore',
        cap: '00000',
        messaggio: `Preventivo ${calcMq} m² - ${calcFormData.tipo}`
      });
      // Track Lead event with Meta Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'Calculator Step 1', content_category: 'Preventivo Calcolatore', value: calcMq, currency: 'EUR' });
      }
      // Track conversion with Google Ads
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { 'send_to': 'AW-17960264240/lead_calculator_step1' });
      }
      setCalcUnlocked(true);
    } catch (error) {
      console.error('Calc form error:', error);
      setCalcUnlocked(true);
    }
  };

  const handleRequestDiscount = async () => {
    if (!tipologiaTetto || !fasciaOraria) {
      alert('Seleziona la tipologia di tetto e la fascia oraria preferita');
      return;
    }
    
    setIsSubmitting(true);
    const pricing = getFullPricing(calcMq, calcFormData.tipo);
    const tipoTettoNome = tipiTetto.find(t => t.id === tipologiaTetto)?.nome || tipologiaTetto;
    const fasciaLabel = fasceOrarie.find(f => f.id === fasciaOraria)?.label || fasciaOraria;
    
    try {
      let photoUrl = '';
      if (fotoTetto) {
        const formData = new FormData();
        formData.append('file', fotoTetto);
        try {
          const uploadRes = await axios.post(`${API}/upload-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          photoUrl = uploadRes.data.url;
        } catch (uploadError) {
          console.error('Photo upload error:', uploadError);
        }
      }
      
      await axios.post(`${API}/leads`, { 
        ...calcFormData, 
        provincia: indirizzo || 'Non specificato',
        cap: '00000',
        tipologia_tetto: tipoTettoNome,
        fascia_oraria: fasciaLabel,
        indirizzo: indirizzo || '',
        coords_lat: coords?.lat || null,
        coords_lng: coords?.lng || null,
        foto_url: photoUrl,
        messaggio: `🏠 PREVENTIVO SCONTATO RICHIESTO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📐 Superficie: ${calcMq} m²
🏢 Tipo Cliente: ${calcFormData.tipo === 'privato' ? 'Privato (IVA 10%)' : 'Azienda (IVA 22%)'}
🏠 Tipologia Tetto: ${tipoTettoNome}
📍 Indirizzo: ${indirizzo || 'Non specificato'}
${coords ? `📌 Coordinate: ${coords.lat}, ${coords.lng}` : ''}
⏰ Fascia Oraria Preferita: ${fasciaLabel}
📷 Foto Allegata: ${photoUrl ? 'Sì' : 'No'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
💰 PREZZO LISTINO: €${pricing.prezzoListino.toLocaleString('it-IT')} (${pricing.ivaLabel} inclusa)
✨ PREZZO SCONTATO: €${pricing.prezzoReale.toLocaleString('it-IT')} (${pricing.ivaLabel} inclusa)
🎉 SCONTO: -${pricing.scontoPercent}% (€${pricing.risparmio.toLocaleString('it-IT')} di risparmio)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
      });
      // Track Lead event with Meta Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'Calculator Discount Request', content_category: 'Preventivo Scontato', value: pricing.prezzoReale, currency: 'EUR' });
      }
      // Track conversion with Google Ads (main conversion - highest value)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { 
          'send_to': 'AW-17960264240/lead_discount_request',
          'value': pricing.prezzoReale,
          'currency': 'EUR'
        });
      }
      setShowFinalPrice(true);
    } catch (error) {
      console.error('Lead submit error:', error);
      setShowFinalPrice(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCalculator = () => {
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };

  // CONTENT DATA
  const problems = [
    { icon: CloudRain, title: "Infiltrazioni d'acqua", desc: "Macchie di umidità, muffa e danni strutturali che peggiorano nel tempo" },
    { icon: Thermometer, title: "Caldo insopportabile", desc: "D'estate la casa diventa un forno, con bollette del condizionatore alle stelle" },
    { icon: Wind, title: "Grandine devastante", desc: "Ogni temporale è un incubo: tegole rotte, danni e riparazioni costose" },
    { icon: CircleDollarSign, title: "Costi di manutenzione", desc: "Interventi continui su guaine che si degradano dopo pochi anni" }
  ];

  const features = [
    { icon: Sparkles, title: "Nanotecnologia Avanzata", desc: "Membrana nanopolimerica di ultima generazione che penetra nelle microporosità creando una barriera molecolare perfetta. Non una semplice guaina, ma un vero e proprio scudo tecnologico.", size: "bento-large" },
    { icon: Leaf, title: "Applicazione Eco-Friendly", desc: "Zero fiamme, zero solventi tossici, zero emissioni. Applicabile a freddo o a caldo in base alle esigenze del cantiere.", size: "" },
    { icon: Shield, title: "Garanzia Fino a 25 Anni", desc: "Certificazioni ETA e BBA. Durata testata in laboratorio oltre 25 anni. Il tuo investimento è protetto.", size: "bento-tall" },
    { icon: Timer, title: "Velocità Record", desc: "Fino a 1000 m² al giorno. Interventi rapidi che non bloccano la tua attività.", size: "" },
    { icon: Thermometer, title: "Range Estremo", desc: "Funziona da -40°C a +200°C. Resiste a shock termici, gelo e calore intenso.", size: "" },
    { icon: Droplets, title: "100% Impermeabile", desc: "Membrana continua senza giunti né saldature. Zero punti deboli dove l'acqua può infiltrarsi.", size: "" }
  ];

  const applications = [
    { 
      icon: Factory, 
      title: "Capannoni Industriali", 
      desc: "Coperture piane e inclinate, lamiera grecata, pannelli sandwich. Applicazione rapida senza interrompere la produzione.",
      benefits: ["Fino a 1000 m²/giorno", "Calpestabile dopo 60 min", "Nessun fermo attività"]
    },
    { 
      icon: Layers, 
      title: "Terrazze e Balconi", 
      desc: "Impermeabilizzazione perfetta per terrazze praticabili, balconi e lastrici solari. Finiture estetiche e antiscivolo.",
      benefits: ["Finiture decorative", "Antiscivolo certificato", "Resistente ai carichi"]
    },
    { 
      icon: Home, 
      title: "Tetti con Tegole e Coppi", 
      desc: "RevoUltra® si applica SOPRA le tegole esistenti, rendendole impermeabili e antigrandine. Nessuna demolizione!",
      benefits: ["Sopra tegole esistenti", "Protezione antigrandine", "Mantiene l'estetica"]
    },
    { 
      icon: Building2, 
      title: "Parcheggi e Scale", 
      desc: "Rivestimento carrabile per parcheggi, rampe e scale esterne. Resistenza a usura, oli e agenti chimici.",
      benefits: ["Carrabile immediato", "Resistente a oli", "Antiscivolo R11"]
    }
  ];

  const whyChooseUs = [
    { icon: Target, title: "Precisione Millimetrica", value: "0.1mm", desc: "Spessore controllato con tecnologia a spruzzo di precisione" },
    { icon: Gauge, title: "Elasticità Estrema", value: "600%", desc: "Si allunga senza rompersi, assorbe movimenti strutturali" },
    { icon: Shield, title: "Resistenza Grandine", value: "65mm", desc: "Testato contro grandine da 65mm a 172 km/h" },
    { icon: Timer, title: "Polimerizzazione", value: "60 sec", desc: "Calpestabile in 60 secondi, carrellabile in 5 minuti" }
  ];

  const applicationMethods = [
    {
      title: "Applicazione a Caldo",
      subtitle: "Per grandi superfici industriali",
      icon: Sun,
      benefits: [
        "Ideale per capannoni e coperture oltre 500 m²",
        "Membrana monolitica senza giunture",
        "Velocità fino a 1000 m²/giorno",
        "Adesione perfetta anche su superfici irregolari",
        "Raggiunge ogni punto, anche i più nascosti"
      ],
      ideal: "Capannoni, tetti industriali, coperture metalliche"
    },
    {
      title: "Applicazione a Freddo",
      subtitle: "Per interventi di precisione",
      icon: Snowflake,
      benefits: [
        "Perfetta per terrazze, balconi e piccole aree",
        "Applicabile manualmente con rullo o spatola",
        "Costi inferiori per superfici ridotte",
        "Ideale per dettagli e punti critici",
        "Nessuna attrezzatura speciale richiesta"
      ],
      ideal: "Terrazzi, balconi, scale, dettagli complessi"
    }
  ];

  const socialProof = [
    { icon: Users, value: "2.847", label: "Clienti Soddisfatti", sublabel: "negli ultimi 3 anni" },
    { icon: Layers, value: "180.000", label: "m² Impermeabilizzati", sublabel: "in tutta Italia" },
    { icon: Star, value: "4.9/5", label: "Valutazione Media", sublabel: "su 1.200+ recensioni" },
    { icon: Award, value: "15", label: "Anni di Esperienza", sublabel: "nel settore" }
  ];

  const testimonials = [
    { name: "Marco R.", role: "Titolare", company: "Officine Zanetti, Verona", text: "Avevamo infiltrazioni da anni. Con RevoUltra® abbiamo impermeabilizzato 1.200 m² di capannone in soli 2 giorni. Zero infiltrazioni da oltre 2 anni. Risultato eccezionale, lo consiglio a tutti.", rating: 5 },
    { name: "Elena P.", role: "Amministratore", company: "Condominio Residence, Brescia", text: "Il terrazzo condominiale perdeva da 10 anni. Dopo decine di riparazioni inutili, RevoUltra® ha risolto il problema definitivamente. Intervento pulito, veloce e professionale. Finalmente possiamo dormire tranquilli!", rating: 5 },
    { name: "Arch. Davide C.", role: "Progettista", company: "Studio C&M, Bologna", text: "Come professionista, ho provato molte soluzioni. RevoUltra® è semplicemente superiore: applicazione veloce, risultato impeccabile, zero problemi post-intervento. Lo specifico in tutti i miei progetti.", rating: 5 },
    { name: "Giuseppe M.", role: "Proprietario", company: "Villa privata, Padova", text: "La grandine aveva distrutto le tegole 3 volte in 5 anni. Dopo RevoUltra® applicato sopra i coppi, l'ultimo temporale non ha fatto un graffio. Avrei dovuto farlo prima!", rating: 5 }
  ];

  const faqs = [
    { q: "Quanto dura realmente RevoUltra®?", a: "RevoUltra® ha una durabilità certificata da test di laboratorio oltre 25 anni. Le certificazioni ETA (European Technical Assessment) e BBA (British Board of Agrément) attestano una vita utile fino a 30 anni in condizioni normali. È una soluzione definitiva, non un rattoppo temporaneo." },
    { q: "Devo demolire la vecchia guaina o le tegole?", a: "Assolutamente NO! Uno dei grandi vantaggi di RevoUltra® è che si applica direttamente sulla superficie esistente: guaine vecchie, tegole, coppi, lamiera, cemento. Nessuna demolizione significa nessuno smaltimento costoso, nessun cantiere lungo e nessun disagio." },
    { q: "Quanto tempo serve per l'applicazione?", a: "Dipende dalla superficie. Un tecnico specializzato applica fino a 1000 m² al giorno con il metodo a caldo. Una terrazza di 50 m² richiede mezza giornata. La membrana è calpestabile dopo soli 60 secondi e completamente carrabile dopo 5 minuti." },
    { q: "Funziona anche su tetti inclinati con tegole?", a: "Sì! RevoUltra® si applica SOPRA le tegole o i coppi esistenti, creando una membrana protettiva continua che rende il tetto impermeabile E antigrandine. L'estetica del tetto rimane invariata, ma la protezione diventa totale." },
    { q: "È compatibile con pannelli fotovoltaici?", a: "Perfettamente compatibile. La versione FR Broof T4 è specificamente certificata per coperture con impianti fotovoltaici. Molti clienti approfittano dell'intervento per preparare il tetto all'installazione dei pannelli." },
    { q: "Che differenza c'è tra applicazione a caldo e a freddo?", a: "L'applicazione a caldo (spray ad alta pressione) è ideale per grandi superfici industriali: veloce, perfetta adesione, raggiunge ogni punto. L'applicazione a freddo (rullo/spatola) è perfetta per terrazze, balconi e dettagli. Il nostro tecnico valuterà la soluzione migliore per il tuo caso specifico durante il sopralluogo gratuito." },
    { q: "Quanto costa?", a: "Il costo dipende da diversi fattori: metratura, tipo di superficie, accessibilità del cantiere. Mediamente l'investimento si recupera in 3-5 anni grazie all'azzeramento dei costi di manutenzione e riparazione. Richiedi un preventivo gratuito: ti sorprenderà scoprire quanto sia accessibile una soluzione definitiva." }
  ];

  const guarantees = [
    { icon: Shield, title: "Garanzia Scritta", desc: "Certificato di garanzia fino a 25 anni su materiali e manodopera" },
    { icon: BadgeCheck, title: "Certificazioni", desc: "ETA, BBA, CE, FR Broof T4 - Standard europei di qualità" },
    { icon: Wrench, title: "Assistenza Post-Vendita", desc: "Supporto tecnico dedicato per qualsiasi necessità futura" },
    { icon: Eye, title: "Sopralluogo Gratuito", desc: "Analisi professionale senza impegno entro 48 ore" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency Banner */}
      <UrgencyBanner />

      {/* Header */}
      <header className={`fixed top-[36px] md:top-[44px] left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'header-blur border-b border-black/5' : 'bg-transparent'}`}>
        <div className="container-main py-3 md:py-4 flex items-center justify-between">
          <a href="/" className="text-xl md:text-3xl">
            <span className={`font-semibold tracking-tight ${scrolled ? 'text-[#1D1D1F]' : 'text-white'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              REVO<span className="text-[#15803d]">ULTRA</span><sup className="text-[8px] md:text-xs">®</sup>
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#problemi" className={`${scrolled ? 'text-[#86868B] hover:text-[#1D1D1F]' : 'text-white/70 hover:text-white'} transition-colors text-sm font-medium`}>Il Problema</a>
            <a href="#soluzione" className={`${scrolled ? 'text-[#86868B] hover:text-[#1D1D1F]' : 'text-white/70 hover:text-white'} transition-colors text-sm font-medium`}>La Soluzione</a>
            <a href="#applicazioni" className={`${scrolled ? 'text-[#86868B] hover:text-[#1D1D1F]' : 'text-white/70 hover:text-white'} transition-colors text-sm font-medium`}>Applicazioni</a>
            <a href="#calculator" className={`${scrolled ? 'text-[#86868B] hover:text-[#1D1D1F]' : 'text-white/70 hover:text-white'} transition-colors text-sm font-medium`}>Preventivo</a>
          </nav>
          <button onClick={scrollToCalculator} className={`${scrolled ? 'bg-[#15803d] text-white' : 'bg-white text-[#1D1D1F]'} font-medium px-3 py-2 md:px-6 md:py-3 rounded-full transition-all hover:scale-105 text-xs md:text-sm`} data-testid="header-cta">
            <span className="hidden sm:inline">Calcola il Tuo Risparmio</span>
            <span className="sm:hidden">Preventivo</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-[36px] md:pt-[44px]" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <video ref={videoRef} autoPlay muted loop playsInline className="w-full h-full object-cover" poster="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80">
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50" />
        </div>

        <div className="container-main w-full relative z-10 pt-20 md:pt-24">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div ref={heroRef} className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-3 py-1.5 md:px-4 md:py-2 mb-4 md:mb-6">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400 text-xs md:text-sm font-medium">Solo 7 sopralluoghi gratuiti rimasti</span>
              </div>
              
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-white mb-4 md:mb-6">
                Il Tuo Tetto Perde?<br />
                <span className="text-[#15803d]">Risolvi per Sempre.</span>
              </h1>
              
              <p className="text-base md:text-xl text-white/80 leading-relaxed max-w-xl mb-4 md:mb-6">
                RevoUltra® è la tecnologia nanopolimerica che impermeabilizza <strong className="text-white">definitivamente</strong> tetti, terrazze e capannoni. 
                <span className="text-[#15803d] font-semibold"> Senza demolire. Senza aspettare. Garantito fino a 25 anni.</span>
              </p>

              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8 text-sm md:text-base">
                {["Nessuna demolizione della vecchia guaina", "Applicabile su tegole, lamiera, cemento", "Calpestabile dopo 60 secondi", "Protezione anche antigrandine"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 md:gap-3 text-white/90">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-[#15803d] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
                <button onClick={scrollToCalculator} className="bg-[#15803d] hover:bg-[#166534] text-white font-semibold text-base md:text-lg px-6 py-3 md:px-8 md:py-4 rounded-full flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-lg shadow-green-900/30" data-testid="hero-primary">
                  Calcola Preventivo Gratis
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button onClick={() => setVideoModalOpen(true)} className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-medium text-base md:text-lg px-6 py-3 md:px-8 md:py-4 rounded-full flex items-center justify-center gap-2 md:gap-3 border border-white/20 transition-all" data-testid="hero-secondary">
                  <Play className="w-5 h-5" />
                  Guarda il Video
                </button>
              </div>
            </div>

            {/* Hero Form */}
            <div className={`transition-all duration-1000 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl md:rounded-3xl p-5 md:p-8 border border-white/20">
                <div className="flex items-center gap-2 mb-3 md:mb-4">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-[#15803d] rounded-full animate-pulse" />
                  <span className="text-[#15803d] text-xs md:text-sm font-semibold">3 persone stanno richiedendo un preventivo</span>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2">Sopralluogo Gratuito</h3>
                <p className="text-white/60 text-sm mb-6">Il tecnico ti ricontatta entro 24 ore</p>
                
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#15803d] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-white font-medium text-xl">Perfetto! Richiesta inviata</p>
                    <p className="text-white/60 text-sm mt-2">Un nostro tecnico specializzato ti ricontatterà entro 24 ore per fissare il sopralluogo gratuito.</p>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="hero-form">
                    <input type="text" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Nome e Cognome *" value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} data-testid="hero-input-nome" />
                    <input type="tel" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Telefono *" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} data-testid="hero-input-telefono" />
                    <input type="text" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Provincia *" value={formData.provincia} onChange={(e) => setFormData({...formData, provincia: e.target.value})} data-testid="hero-input-provincia" />
                    <button type="submit" className="w-full bg-[#15803d] hover:bg-[#166534] text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" data-testid="hero-form-submit">
                      Voglio il Sopralluogo Gratuito
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <p className="text-white/40 text-xs text-center">Nessun impegno • Il tecnico ti richiama entro 24h • Preventivo gratuito</p>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-white/10">
            {socialProof.map((item, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">{item.value}</p>
                <p className="text-white/80 text-sm font-medium">{item.label}</p>
                <p className="text-white/50 text-xs">{item.sublabel}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block">
          <ChevronDown className="w-8 h-8 text-white/60" />
        </div>
      </section>

      {/* Problems Section */}
      <section id="problemi" className="py-20 md:py-28 bg-[#1D1D1F]" data-testid="problems-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-red-400 tracking-wide uppercase mb-4">Il Problema</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Stanco di Rattoppi che Non Funzionano?
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              Ogni anno milioni di euro vengono sprecati in riparazioni temporanee. Guaine che si sollevano, tegole che si rompono, infiltrazioni che tornano. <strong className="text-white">È ora di risolvere il problema alla radice.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-red-500/30 transition-all">
                <div className="w-14 h-14 bg-red-500/20 rounded-2xl flex items-center justify-center mb-4">
                  <problem.icon className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{problem.title}</h3>
                <p className="text-white/60">{problem.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-white/80 text-lg mb-6">
              Se ti riconosci in almeno uno di questi problemi, <span className="text-[#15803d] font-semibold">RevoUltra® è la soluzione che stavi cercando.</span>
            </p>
            <button onClick={scrollToCalculator} className="bg-[#15803d] hover:bg-[#166534] text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 transition-all hover:scale-105">
              Scopri Come Risolvere
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="soluzione" className="py-20 md:py-28 bg-white" data-testid="solution-section">
        <div className="container-main">
          <div ref={featuresRef} className={`transition-all duration-1000 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-16">
              <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">La Soluzione Definitiva</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
                RevoUltra®: La Tecnologia che Fa la Differenza
              </h2>
              <p className="text-[#86868B] text-lg max-w-3xl mx-auto">
                Non una semplice guaina, ma una <strong className="text-[#1D1D1F]">membrana nanopolimerica di ultima generazione</strong> che crea una barriera molecolare impenetrabile. Testata, certificata, garantita.
              </p>
            </div>

            <div className="bento-grid">
              {features.map((feature, i) => (
                <div key={i} className={`bento-item ${feature.size}`} data-testid={`feature-${i}`} style={{ animationDelay: `${i * 100}ms` }}>
                  <div>
                    <div className="w-14 h-14 bg-[#dcfce7] rounded-2xl flex items-center justify-center mb-6">
                      <feature.icon className="w-7 h-7 text-[#15803d]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">{feature.title}</h3>
                    <p className="text-[#86868B] leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-20 md:py-28 bg-[#F5F5F7]" data-testid="specs-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Performance Tecniche</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              Numeri che Parlano da Soli
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((spec, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 text-center shadow-sm">
                <div className="w-16 h-16 bg-[#dcfce7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <spec.icon className="w-8 h-8 text-[#15803d]" />
                </div>
                <p className="text-4xl md:text-5xl font-bold text-[#15803d] mb-2">{spec.value}</p>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{spec.title}</h3>
                <p className="text-[#86868B] text-sm">{spec.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Methods */}
      <section className="py-20 md:py-28 bg-white" data-testid="methods-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Metodo di Applicazione</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              A Caldo o a Freddo? Scegliamo Insieme
            </h2>
            <p className="text-[#86868B] text-lg max-w-3xl mx-auto">
              Il nostro tecnico valuterà durante il sopralluogo gratuito quale metodo è più adatto alle tue esigenze. Entrambi garantiscono risultati eccellenti.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {applicationMethods.map((method, i) => (
              <div key={i} className="bg-[#F5F5F7] rounded-3xl p-8 md:p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#dcfce7] rounded-2xl flex items-center justify-center">
                    <method.icon className="w-8 h-8 text-[#15803d]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-[#1D1D1F]">{method.title}</h3>
                    <p className="text-[#86868B]">{method.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {method.benefits.map((benefit, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#15803d] mt-0.5 flex-shrink-0" />
                      <span className="text-[#1D1D1F]">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-[#86868B]"><strong className="text-[#1D1D1F]">Ideale per:</strong> {method.ideal}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section id="applicazioni" className="py-20 md:py-28 bg-[#1D1D1F]" data-testid="applications-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Dove Si Applica</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Una Soluzione per Ogni Esigenza
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              RevoUltra® si adatta perfettamente a qualsiasi superficie: cemento, lamiera, tegole, coppi, guaine esistenti. Nessuna limitazione.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {applications.map((app, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-[#15803d]/50 transition-all">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#15803d]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <app.icon className="w-7 h-7 text-[#15803d]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{app.title}</h3>
                    <p className="text-white/60">{app.desc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {app.benefits.map((benefit, j) => (
                    <span key={j} className="bg-[#15803d]/20 text-[#15803d] text-sm font-medium px-3 py-1 rounded-full">
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-3xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-amber-400" />
              <h3 className="text-2xl font-semibold text-white">Protezione Antigrandine Certificata</h3>
            </div>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-6">
              RevoUltra® applicato <strong className="text-white">sopra tegole e coppi</strong> resiste a grandine fino a 65mm di diametro a 172 km/h. 
              Il tuo tetto rimane esteticamente intatto ma diventa <span className="text-amber-400 font-semibold">virtualmente indistruttibile</span>.
            </p>
            <button onClick={scrollToCalculator} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 transition-all hover:scale-105">
              Proteggi il Tuo Tetto dalla Grandine
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white" data-testid="how-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Il Processo</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              3 Passi verso la Tranquillità
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Sopralluogo Gratuito", desc: "Un tecnico specializzato analizza la tua copertura, identifica le criticità e definisce il ciclo applicativo ottimale. Nessun impegno, nessun costo.", icon: Eye },
              { step: "02", title: "Applicazione Rapida", desc: "Spruzzo a caldo o applicazione a freddo, in base alle esigenze. Nessuna demolizione, nessun cantiere invasivo. Fino a 1000 m² in un solo giorno.", icon: Wrench },
              { step: "03", title: "Protezione Garantita", desc: "Membrana continua, impermeabile, calpestabile in 60 secondi. Certificazione scritta di garanzia fino a 25 anni. Fine dei problemi.", icon: Shield }
            ].map((item, i) => (
              <div key={i} className="bg-[#F5F5F7] rounded-3xl p-8 md:p-10 relative" data-testid={`step-${i}`}>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-6xl font-bold text-[#E5E5E5]">{item.step}</span>
                </div>
                <div className="w-14 h-14 bg-[#dcfce7] rounded-2xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-[#15803d]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">{item.title}</h3>
                <p className="text-[#86868B] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="calculator-section py-20 md:py-28" data-testid="calculator-section">
        <div className="container-main">
          <div ref={calcRef} className={`transition-all duration-1000 ${calcVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#15803d]/20 border border-[#15803d]/30 rounded-full px-4 py-2 mb-6">
                <Phone className="w-4 h-4 text-[#15803d]" />
                <span className="text-[#15803d] text-sm font-medium">Ti ricontattiamo entro 24 ore con il preventivo</span>
              </div>
              <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Richiedi Preventivo Gratuito</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
                Sopralluogo + Preventivo Gratis
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                Compila il form e ricevi un <strong className="text-white">preventivo personalizzato</strong> direttamente dal nostro tecnico. <strong className="text-white">Nessun impegno.</strong>
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/10">
                {/* Slider */}
                <div className="mb-8">
                  <div className="flex justify-between items-end mb-4">
                    <label className="text-white/80 font-medium">Quanti m² devi impermeabilizzare?</label>
                    <span className="text-4xl font-semibold text-white">{calcMq} m²</span>
                  </div>
                  <input type="range" min="50" max="2000" step="10" value={calcMq} onChange={(e) => setCalcMq(parseInt(e.target.value))} className="w-full h-2 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:bg-[#15803d] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer" data-testid="calc-slider" />
                  <div className="flex justify-between text-sm text-white/40 mt-2">
                    <span>50 m²</span>
                    <span>2000 m²</span>
                  </div>
                </div>

                {!calcUnlocked ? (
                  <div className="bg-white/10 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-2">Richiedi il Tuo Preventivo Gratuito</h3>
                    <p className="text-white/60 mb-6">Un nostro tecnico ti contatterà con il preventivo personalizzato</p>
                    
                    <form onSubmit={handleCalcFormSubmit} className="max-w-md mx-auto space-y-4" data-testid="calc-form">
                      <input type="text" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Nome e Cognome *" value={calcFormData.nome} onChange={(e) => setCalcFormData({...calcFormData, nome: e.target.value})} data-testid="calc-input-nome" />
                      <input type="tel" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Telefono *" value={calcFormData.telefono} onChange={(e) => setCalcFormData({...calcFormData, telefono: e.target.value})} data-testid="calc-input-telefono" />
                      <input type="email" required className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Email *" value={calcFormData.email} onChange={(e) => setCalcFormData({...calcFormData, email: e.target.value})} data-testid="calc-input-email" />
                      
                      <div className="grid grid-cols-2 gap-4">
                        <button type="button" onClick={() => setCalcFormData({...calcFormData, tipo: 'privato'})} className={`p-4 rounded-xl border-2 transition-all ${calcFormData.tipo === 'privato' ? 'border-[#15803d] bg-[#15803d]/20 text-white' : 'border-white/20 text-white/60 hover:border-white/40'}`}>
                          <Users className="w-6 h-6 mx-auto mb-2" />
                          <span className="font-medium">Privato</span>
                        </button>
                        <button type="button" onClick={() => setCalcFormData({...calcFormData, tipo: 'azienda'})} className={`p-4 rounded-xl border-2 transition-all ${calcFormData.tipo === 'azienda' ? 'border-[#15803d] bg-[#15803d]/20 text-white' : 'border-white/20 text-white/60 hover:border-white/40'}`}>
                          <Building2 className="w-6 h-6 mx-auto mb-2" />
                          <span className="font-medium">Azienda</span>
                        </button>
                      </div>

                      <button type="submit" className="w-full bg-[#15803d] hover:bg-[#166534] text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-all" data-testid="calc-form-submit">
                        Continua
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </form>
                    <p className="text-white/40 text-xs mt-4">I tuoi dati sono al sicuro • Nessuno spam</p>
                  </div>
                ) : !showFinalPrice ? (
                  <div>
                    {(() => {
                      return (
                        <>
                          <div className="bg-gradient-to-r from-[#15803d]/20 to-emerald-500/20 border border-[#15803d]/30 rounded-2xl p-6 mb-6 text-center">
                            <CheckCircle2 className="w-12 h-12 text-[#15803d] mx-auto mb-3" />
                            <h3 className="text-xl font-semibold text-white mb-2">Ottimo {calcFormData.nome.split(' ')[0]}! 🎉</h3>
                            <p className="text-white/70">Completa questi ultimi dettagli per ricevere il preventivo più accurato</p>
                            <p className="text-white/50 text-sm mt-2">Superficie: <strong className="text-white">{calcMq} m²</strong> • {calcFormData.tipo === 'privato' ? 'Cliente Privato' : 'Azienda'}</p>
                          </div>

                          <div className="mb-6">
                            <p className="text-white/80 font-medium mb-3 text-center">Che tipo di copertura hai? *</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {tipiTetto.map((tipo) => (
                                <button key={tipo.id} type="button" onClick={() => setTipologiaTetto(tipo.id)} className={`p-4 rounded-xl border-2 transition-all ${tipologiaTetto === tipo.id ? 'border-[#15803d] bg-[#15803d]/20 text-white' : 'border-white/20 text-white/60 hover:border-white/40'}`}>
                                  <tipo.icon className="w-8 h-8 mx-auto mb-2" />
                                  <span className="text-sm font-medium">{tipo.nome}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <p className="text-white/80 font-medium mb-3 text-center">Carica una foto del tetto (opzionale)</p>
                            <div className="flex flex-col items-center gap-4">
                              <label className="w-full max-w-md cursor-pointer">
                                <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${fotoPreview ? 'border-[#15803d]' : 'border-white/30 hover:border-white/50'}`}>
                                  {fotoPreview ? (
                                    <div className="relative">
                                      <img src={fotoPreview} alt="Preview" className="max-h-32 mx-auto rounded-lg" />
                                      <p className="text-[#15803d] text-sm mt-2">✓ Foto caricata</p>
                                    </div>
                                  ) : (
                                    <>
                                      <Layers className="w-10 h-10 mx-auto mb-2 text-white/40" />
                                      <p className="text-white/60 text-sm">Clicca per caricare una foto</p>
                                      <p className="text-white/40 text-xs mt-1">JPG, PNG (max 5MB)</p>
                                    </>
                                  )}
                                </div>
                                <input type="file" accept="image/*" onChange={handleFotoUpload} className="hidden" />
                              </label>
                            </div>
                          </div>

                          <div className="mb-6">
                            <p className="text-white/80 font-medium mb-3 text-center">Dove si trova l'immobile?</p>
                            <div className="max-w-md mx-auto">
                              <div className="relative">
                                <input type="text" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 pr-12 text-white placeholder-white/50 focus:outline-none focus:border-[#15803d] transition-colors" placeholder="Via, Città, CAP" />
                                <button type="button" onClick={getLocation} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/60 hover:text-[#15803d] transition-colors" title="Usa la mia posizione">
                                  <MapPin className="w-5 h-5" />
                                </button>
                              </div>
                              {coords && <p className="text-[#15803d] text-xs mt-2 text-center">✓ Posizione rilevata</p>}
                            </div>
                          </div>

                          <div className="mb-6">
                            <p className="text-white/80 font-medium mb-3 text-center">Quando preferisci essere ricontattato? *</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-lg mx-auto">
                              {fasceOrarie.map((fascia) => (
                                <button key={fascia.id} type="button" onClick={() => setFasciaOraria(fascia.id)} className={`p-3 rounded-xl border-2 transition-all text-sm ${fasciaOraria === fascia.id ? 'border-[#15803d] bg-[#15803d]/20 text-white' : 'border-white/20 text-white/60 hover:border-white/40'}`}>
                                  {fascia.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <div className="bg-gradient-to-r from-[#15803d]/20 to-emerald-500/20 border border-[#15803d]/30 rounded-2xl p-6 mb-6 text-center">
                            <div className="flex items-center justify-center gap-2 mb-3">
                              <Phone className="w-5 h-5 text-[#15803d]" />
                              <span className="text-[#15803d] font-semibold">Quasi fatto!</span>
                            </div>
                            <p className="text-white/80 mb-4">
                              Completa la richiesta e un nostro <strong className="text-white">tecnico specializzato</strong> ti contatterà entro 24 ore con il <strong className="text-white">preventivo personalizzato</strong>.
                            </p>
                            <p className="text-white/60 text-sm mb-4">
                              ⚡ Solo <strong className="text-[#15803d]">7 sopralluoghi gratuiti</strong> disponibili questo mese
                            </p>
                            
                            <button onClick={handleRequestDiscount} disabled={isSubmitting} className={`w-full bg-[#15803d] text-white font-semibold py-4 px-8 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#166534] hover:scale-[1.02]'}`} data-testid="request-discount-btn">
                              {isSubmitting ? (
                                <>
                                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Invio in corso...
                                </>
                              ) : (
                                <>
                                  Invia Richiesta Preventivo
                                  <ArrowRight className="w-5 h-5" />
                                </>
                              )}
                            </button>
                          </div>
                          
                          <p className="text-white/40 text-xs text-center">Nessun impegno • Ti ricontattiamo entro 24h</p>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center">
                    {(() => {
                      return (
                        <>
                          <div className="mb-6">
                            <div className="w-20 h-20 bg-[#15803d] rounded-full flex items-center justify-center mx-auto mb-4">
                              <CheckCircle2 className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-2xl font-semibold text-white mb-2">Richiesta Inviata! 🎉</h3>
                          </div>

                          <div className="bg-gradient-to-r from-[#15803d]/20 to-emerald-500/20 border border-[#15803d]/30 rounded-2xl p-6 mb-6">
                            <p className="text-white text-lg leading-relaxed">
                              <strong>{calcFormData.nome.split(' ')[0]}</strong>, grazie per la tua richiesta!<br /><br />
                              Un nostro <strong className="text-[#15803d]">tecnico specializzato</strong> ti contatterà entro <strong>24 ore</strong> al numero <strong>{calcFormData.telefono}</strong> per:<br /><br />
                              ✓ Fornirti il <strong className="text-white">preventivo personalizzato</strong><br />
                              ✓ Fissare il sopralluogo gratuito<br />
                              ✓ Rispondere a tutte le tue domande
                            </p>
                          </div>

                          <div className="flex items-center justify-center gap-2 text-white/60 text-sm mb-6">
                            <Phone className="w-4 h-4" />
                            <span>Ti richiameremo nella fascia oraria: {fasceOrarie.find(f => f.id === fasciaOraria)?.label}</span>
                          </div>

                          <button onClick={scrollToForm} className="bg-white text-[#1D1D1F] font-medium px-10 py-4 rounded-full hover:scale-105 transition-transform" data-testid="calc-final-cta">
                            Oppure Scrivici Ora
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-20 md:py-28 bg-[#F5F5F7]" data-testid="guarantees-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Le Nostre Garanzie</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              Investimento Protetto, Zero Rischi
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {guarantees.map((g, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 bg-[#dcfce7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <g.icon className="w-7 h-7 text-[#15803d]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{g.title}</h3>
                <p className="text-[#86868B] text-sm">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Testimonianze Verificate</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mb-6">
              Dicono di Noi
            </h2>
            <p className="text-[#86868B] text-lg">
              Oltre 2.800 clienti soddisfatti in tutta Italia. Ecco cosa dicono.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                  ))}
                </div>
                <p className="text-[#1D1D1F] text-lg leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#15803d] rounded-full flex items-center justify-center">
                    <span className="text-lg font-semibold text-white">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#1D1D1F]">{t.name}</p>
                    <p className="text-sm text-[#86868B]">{t.role}, {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#F5F5F7]" data-testid="faq-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-sm font-medium text-[#15803d] tracking-wide uppercase mb-4">Domande Frequenti</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                Hai Dubbi? Rispondiamo Noi
              </h2>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10">
              {faqs.map((faq, i) => (
                <FAQItem key={i} question={faq.q} answer={faq.a} isOpen={openFAQ === i} onClick={() => setOpenFAQ(openFAQ === i ? null : i)} />
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-[#86868B] mb-4">Hai altre domande? Parliamone!</p>
              <button onClick={scrollToForm} className="bg-[#15803d] hover:bg-[#166534] text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 transition-all hover:scale-105">
                Contattaci Ora
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#1D1D1F]" data-testid="final-cta">
        <div className="container-main text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Ultimi posti disponibili per sopralluogo gratuito</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Pronto a Dire Addio alle Infiltrazioni?
            </h2>
            <p className="text-white/70 text-lg mb-8">
              Non aspettare il prossimo temporale. Richiedi ora il tuo sopralluogo gratuito e scopri come RevoUltra® può proteggere il tuo immobile <strong className="text-white">per sempre</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToCalculator} className="bg-[#15803d] hover:bg-[#166534] text-white font-semibold text-lg px-10 py-5 rounded-full inline-flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-lg shadow-green-900/30">
                Calcola il Tuo Preventivo Gratis
                <ArrowRight className="w-5 h-5" />
              </button>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="bg-white/10 hover:bg-white/20 text-white font-semibold text-lg px-10 py-5 rounded-full inline-flex items-center justify-center gap-2 transition-all border border-white/20">
                <Phone className="w-5 h-5" />
                Chiama Ora: {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 md:py-28 bg-white" data-testid="form-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                Richiedi Informazioni
              </h2>
              <p className="text-[#86868B] text-lg mt-4">Compila il form e ti ricontattiamo entro 24 ore.</p>
            </div>

            {formSubmitted ? (
              <div className="text-center py-16 bg-[#F5F5F7] rounded-3xl" data-testid="form-success">
                <div className="w-20 h-20 bg-[#dcfce7] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-[#15803d]" />
                </div>
                <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-2">Richiesta Inviata!</h3>
                <p className="text-[#86868B]">Ti contatteremo entro 24 ore per il sopralluogo gratuito.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-6" data-testid="lead-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Nome Completo</label>
                    <input type="text" required className="input-apple" placeholder="Mario Rossi" value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} data-testid="input-nome" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Telefono</label>
                    <input type="tel" required className="input-apple" placeholder="+39 333 1234567" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} data-testid="input-telefono" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Email</label>
                  <input type="email" required className="input-apple" placeholder="mario@email.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="input-email" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Provincia</label>
                  <input type="text" required className="input-apple" placeholder="Es. Verona" value={formData.provincia} onChange={(e) => setFormData({...formData, provincia: e.target.value})} data-testid="input-provincia" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Descrivi il tuo progetto (opzionale)</label>
                  <textarea className="input-apple min-h-[120px] resize-none" placeholder="Es. Ho un capannone di 500mq con infiltrazioni..." value={formData.messaggio} onChange={(e) => setFormData({...formData, messaggio: e.target.value})} data-testid="input-messaggio" />
                </div>

                <button type="submit" className="btn-primary w-full text-lg py-5" data-testid="form-submit">
                  Invia Richiesta
                </button>

                <p className="text-center text-sm text-[#86868B]">
                  Inviando accetti la nostra privacy policy. Nessuno spam, promesso.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1D1D1F] text-white py-16" data-testid="footer">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="mb-6 text-3xl">
                <BrandText variant="dark" />
              </div>
              <p className="text-white/60 leading-relaxed max-w-md mb-4">
                Sistema impermeabilizzante con tecnologia nanopolimerica per tetti, terrazze, capannoni e coperture di ogni tipo. Protezione definitiva certificata fino a 25 anni.
              </p>
              <p className="text-white/40 text-sm">
                Un servizio di <strong className="text-white/60">{COMPANY.name}</strong>
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contatti</h4>
              <div className="space-y-3 text-white/60 text-sm">
                <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> {COMPANY.phone} (Numero Verde)
                </a>
                <a href={`https://wa.me/39${COMPANY.whatsapp.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> WhatsApp: {COMPANY.whatsapp}
                </a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" /> {COMPANY.email}
                </a>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {COMPANY.address}
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Certificazioni</h4>
              <div className="flex flex-wrap gap-2 mb-6">
                {['ETA', 'BBA', 'CE', 'FR Broof T4'].map((cert, i) => (
                  <span key={i} className="bg-white/10 px-3 py-1 rounded-full text-sm text-white/80">{cert}</span>
                ))}
              </div>
              <h4 className="font-semibold mb-3">Altri Servizi</h4>
              <div className="space-y-2 text-sm">
                <a href="https://evoluzionetetto.it" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-white transition-colors">Evoluzione Tetto®</a>
                <a href="https://isoliamo.it" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-white transition-colors">Isoliamo®</a>
                <a href="https://revoquota.it" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-white transition-colors">RevoQuota Engineering®</a>
                <a href="https://revogreenitalia.com" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-white transition-colors">RevoGreen Italia</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="text-white/40 text-sm space-y-1">
                <p>© {new Date().getFullYear()} {COMPANY.name} - Tutti i diritti riservati</p>
                <p>{COMPANY.piva} | Sede: {COMPANY.address}</p>
              </div>
              <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-white/40">
                <a href="https://revogreenitalia.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="https://revogreenitalia.com/cookie-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Cookie Policy</a>
                <a href="https://revogreenitalia.com/termini-e-condizioni/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Termini e Condizioni</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <VideoModal isOpen={videoModalOpen} onClose={() => setVideoModalOpen(false)} />
    </div>
  );
}

export default App;
